import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Grabels' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Grabels',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Grabels' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Grabels. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous sur les menuiseries anciennes et récentes à Grabels ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, Grabels mêle maisons de village anciennes et résidences récentes. Nous intervenons sur les deux : fenêtres bois du centre ancien comme baies coulissantes des lotissements. Devis gratuit, 24-48h.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est votre délai à Grabels ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Notre délai standard est de 24 à 48h, 7j/7. Nous couvrons tout Grabels : centre village, val de Mosson, lotissements. Pour une urgence vitrier, appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function GrabelsPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Grabels | DFPRO"
        description="Baie coulissante, fenêtre ancienne ou volet roulant en panne à Grabels ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-grabels/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Grabels"
        subtitle="Du centre village aux résidences récentes du val de Mosson : nos techniciens réparent vos menuiseries à Grabels. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Fenêtre ancienne ou baie récente en panne à Grabels ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Grabels et dans le nord-ouest montpelliérain."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Grabels, au nord-ouest de Montpellier, associe un centre village ancien et des résidences récentes le long du val de Mosson. Nous intervenons sur tous types de menuiseries. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grabels-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Grabels"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante dans les résidences récentes</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les lotissements de Grabels sont équipés de baies coulissantes. Quand les roulettes s'usent, la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> ou frotte. Un changement de galets redonne une coulisse fluide et silencieuse.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Fenêtre ancienne : crémone et joints</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Le centre ancien de Grabels compte des fenêtres en bois qui méritent une attention particulière. <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Crémone</Link> grippée, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> qui ne sont plus étanches : nous réparons avec soin.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Volets roulants</Link> : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">blocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <Link to="/reparation-fenetre-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage fenêtre <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grabels-volet-02.jpg"
              alt="Réparation de fenêtre à Grabels"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grabels-vitrage-03.webp"
              alt="Remplacement de vitrage à Grabels"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et double vitrage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> 7j/7.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Grabels sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Grabels : centre village, val de Mosson, lotissements. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-saint-gely-du-fesc/" className="text-accent-500 font-semibold hover:underline">Saint-Gély-du-Fesc</Link>, Juvignac. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}